import {Exclude, Expose, instanceToPlain, plainToClass} from "class-transformer";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {GeoCoord, GeoCoordValidator} from "../geo-coord";

export const PipelineGraphEdgeValidator = t.intersection([
  t.type({
    id: t.string,
    pipelineGraphId: t.string,
    fromPipelineNodeId: t.string,
    toPipelineNodeId: t.string,
  }),
  t.partial({
    geoData: t.array(GeoCoordValidator),
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const PipelineGraphEdgeArrayValidator = t.array(
  PipelineGraphEdgeValidator
);

export type PipelineGraphEdge = t.TypeOf<typeof PipelineGraphEdgeValidator>;
export type PipelineGraphEdgeArray = t.TypeOf<
  typeof PipelineGraphEdgeArrayValidator
>;

@Exclude()
export class PipelineGraphEdgeEntity implements Entity {
  @Expose() id!: string;
  @Expose() pipelineGraphId!: string;
  @Expose() fromPipelineNodeId!: string;
  @Expose() toPipelineNodeId!: string;
  @Expose() geoData?: GeoCoord[];
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;

  readonly entityType = 'PIPELINEGRAPHEDGE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for PipelineGraphEdgeEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static builder(id: string = uuid()): GraphBuilderStep {
    return new PipelineGraphEdgeBuilder(id);
  }

  static create(builder: PipelineGraphEdgeBuilder): PipelineGraphEdgeEntity {
    const entity = new PipelineGraphEdgeEntity();
    entity.id = builder.id;
    entity.pipelineGraphId = builder.pipelineGraphId;
    entity.fromPipelineNodeId = builder.fromPipelineGraphNodeId;
    entity.toPipelineNodeId = builder.toPipelineGraphNodeId;
    entity.geoData = builder.geoData;
    entity.createDttm = builder.createDttm;
    entity.lastModifiedDttm = builder.lastModifiedDttm;
    return entity;
  }

  static from(edge: PipelineGraphEdge): PipelineGraphEdgeEntity {
    const entity: PipelineGraphEdgeEntity = plainToClass(
      PipelineGraphEdgeEntity,
      edge,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  loadPlainValue(): Promise<PipelineGraphEdge> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(PipelineGraphEdgeValidator, object);
  }

  asPlainValue(): PipelineGraphEdge {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(PipelineGraphEdgeValidator, object);
  }
}

interface GraphBuilderStep {
  setPipelineGraph: (value: string) => NodeBuilderStep;
}

interface NodeBuilderStep {
  setNodes: (
    fromPipelineGraphNodeId: string,
    toPipelineGraphNodeId: string
  ) => PipelineGraphEdgeBuilder;
}

class PipelineGraphEdgeBuilder implements GraphBuilderStep, NodeBuilderStep {
  id: string;
  pipelineGraphId!: string;
  fromPipelineGraphNodeId!: string;
  toPipelineGraphNodeId!: string;
  geoData?: GeoCoord[] = undefined;
  createDttm?: number = undefined;
  lastModifiedDttm?: number = undefined;

  constructor(id: string = uuid()) {
    this.id = id;
  }

  setPipelineGraph(value: string): NodeBuilderStep {
    this.pipelineGraphId = value;
    return this;
  }

  setNodes(
    fromPipelineGraphNodeId: string,
    toPipelineGraphNodeId: string
  ): PipelineGraphEdgeBuilder {
    this.fromPipelineGraphNodeId = fromPipelineGraphNodeId;
    this.toPipelineGraphNodeId = toPipelineGraphNodeId;
    return this;
  }

  setGeoData(value: GeoCoord[] | undefined): PipelineGraphEdgeBuilder {
    this.geoData = value;
    return this;
  }

  setCreateDttm(value: number | undefined): PipelineGraphEdgeBuilder {
    this.createDttm = value;
    return this;
  }

  setLastModifiedDttm(value: number | undefined): PipelineGraphEdgeBuilder {
    this.lastModifiedDttm = value;
    return this;
  }

  /* istanbul ignore next */
  private isValid(): boolean {
    return (
      this.id != undefined &&
      this.pipelineGraphId != undefined &&
      this.fromPipelineGraphNodeId != undefined &&
      this.toPipelineGraphNodeId != undefined
    );
  }

  build(): PipelineGraphEdgeEntity {
    if (!this.isValid()) {
      throw Error(
        'The following fields must be defined: ID, PipelineGraphID, FromPipelineGraphNodeID, & ToPipelineGraphNodeID.'
      );
    }

    return PipelineGraphEdgeEntity.create(this);
  }
}
